// Authentication service for API calls
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

const API_BASE_URL = API_CONFIG.API_BASE_URL;

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

/**
 * Mints a fresh Shay access token from the long-lived refresh token in localStorage.
 * Most of this app authenticates its own API calls with the refresh token directly
 * (see channelService.ts etc.), so the short-lived access token can expire silently
 * in the background — surfacing only where something strictly requires it (e.g. the
 * Node OAuth-connect routes, which reject a refresh token by JWT `type`). Request/
 * response contract confirmed live against POST /api/v1/auth/refresh (422 on missing
 * `refresh_token`, 401 with {detail} string on an invalid one). Returns the new
 * access token on success, or null if there's no refresh token or the refresh call
 * fails — callers should treat null as "user must log in again".
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const data: RefreshTokenResponse = await response.json();
    if (!data.access_token) {
      return null;
    }

    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
    }

    return data.access_token;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    return null;
  }
};

const hasUsableAccessToken = (token: string): boolean => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
    return typeof decoded.exp === 'number' && decoded.exp > Math.floor(Date.now() / 1000) + 30;
  } catch {
    return false;
  }
};

/**
 * Return a Node OAuth-proxy bearer header, silently renewing the Shay access
 * token when it is missing or close to expiry. The Node proxy deliberately
 * rejects the long-lived refresh token, so callers must use this helper rather
 * than sending STORAGE_KEYS.REFRESH_TOKEN as a bearer token.
 */
export const getFreshNodeApiHeaders = async (): Promise<Record<string, string>> => {
  const currentAccessToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (currentAccessToken && hasUsableAccessToken(currentAccessToken)) {
    return { Authorization: `Bearer ${currentAccessToken}` };
  }

  const refreshedAccessToken = await refreshAccessToken();
  const accessToken = refreshedAccessToken || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

export interface RegisterRequest {
  invite_id?: string;
  name: string;
  email_id: string;
  password: string;
  confirm_password: string;
  company_id: string;
  role: string;
  encrypted?: boolean;
}

export interface LoginRequest {
  email_id: string;
  password: string;
  encrypted?: boolean;
}

export interface RegisterResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email_id: string;
  name: string;
  /** Display name when API distinguishes it from login username */
  full_name?: string;
  role: string;
  company_id: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email_id: string;
  name: string;
  /** Display name when API distinguishes it from login username */
  full_name?: string;
  role: string;
  company_id: string;
  /** 'sso' when authenticated via Single Sign-On; absent for email+password logins */
  login_type?: string;
}

export interface ForgotPasswordRequest {
  base_url: string;
  email_id: string;
  app_name?: string;
  template_id?: string;
}

export interface ResetPasswordRequest {
  base_url: string;
  email_id: string;
  user_id: string;
  new_password: string;
  confirm_new_password: string;
  encrypted?: boolean;
}

export interface ForgotPasswordResponse {
  status: string;
  message: string;
}

export interface ResetPasswordResponse {
  status: string;
  message: string;
}

export const registerUser = async (data: RegisterRequest): Promise<RegisterResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.REGISTER}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        // Note: Authorization header will be added by the calling function
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Registration failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const registerUserWithAuth = async (
  data: RegisterRequest, 
  authToken: string
): Promise<RegisterResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.REGISTER}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Registration failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const loginUser = async (data: LoginRequest): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGIN}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats from API (string, array, or object with message)
      let errorMessage = `Login failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        } else if (typeof errorData.detail === 'object' && errorData.detail !== null && errorData.detail.message) {
          // detail is object with message (e.g. { message: "Account is inactive...", error_code: "E005" })
          errorMessage = errorData.detail.message;
        }
      }
      // Fallback to top-level message if we still have the generic status message
      if (errorMessage === `Login failed: ${response.status}` && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const forgetPassword = async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.FORGOT_PASSWORD}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.log('Forgot password error data:', errorData);
      
      // Handle different error detail formats
      let errorMessage = `Forgot password failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

export const resetPassword = async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.RESET_PASSWORD}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.log('Reset password error data:', errorData);
      
      // Handle different error detail formats
      let errorMessage = `Reset password failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export interface SendVerificationEmailRequest {
  email_id: string;
  base_url?: string;
}

export interface SendVerificationEmailResponse {
  status: string;
  message: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  email_id: string;
  name: string;
  role: string;
  company_id: string;
  default_workspace_id?: string | null;
}

export const sendVerificationEmail = async (data: SendVerificationEmailRequest): Promise<SendVerificationEmailResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.SEND_VERIFICATION_EMAIL}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Failed to send verification email: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Send verification email error:', error);
    throw error;
  }
};

export const verifyEmail = async (data: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.VERIFY_EMAIL}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Email verification failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData: VerifyEmailResponse = await response.json();
    
    // Store tokens and user data in localStorage
    if (responseData.access_token && responseData.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, responseData.access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, responseData.refresh_token);
      
      // Store full response for profile and future use
      try {
        localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(responseData));
      } catch (error) {
        console.error('Error storing user data:', error);
      }
      
      // Store company_id if available
      if (responseData.company_id) {
        localStorage.setItem('companyId', responseData.company_id);
      }
      
      // Store default_workspace_id if available
      if (responseData.default_workspace_id) {
        localStorage.setItem('defaultWorkspaceId', responseData.default_workspace_id);
      }
    }

    return responseData;
  } catch (error) {
    console.error('Email verification error:', error);
    throw error;
  }
};

/** Allowed body fields for PUT /user-auth/profile */
export interface UpdateCurrentUserProfileRequest {
  name?: string;
  avatar_url?: string;
  preferences?: Record<string, unknown>;
}

/** User object returned after profile update (shape may include extra read-only fields) */
export interface ProfileUpdateApiUser {
  id?: string;
  user_id?: string;
  name?: string;
  full_name?: string;
  email_id?: string;
  role?: string;
}

export interface UpdateCurrentUserProfileResponse {
  message?: string;
  updated_fields?: string[];
  user?: ProfileUpdateApiUser;
}

/**
 * Updates the current user's personal info (name, avatar_url, preferences).
 * Requires Bearer token (refresh or access, per backend).
 */
export const updateCurrentUserProfile = async (
  body: UpdateCurrentUserProfileRequest,
  token: string
): Promise<UpdateCurrentUserProfileResponse> => {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.USER_AUTH.PROFILE}`, {
    method: 'PUT',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = (await response.json().catch(() => ({}))) as UpdateCurrentUserProfileResponse & {
    detail?: string | Array<{ msg?: string }>;
    message?: string;
  };

  if (!response.ok) {
    let errorMessage = `Profile update failed: ${response.status}`;
    if (typeof data.message === 'string') {
      errorMessage = data.message;
    } else if (data.detail) {
      if (typeof data.detail === 'string') {
        errorMessage = data.detail;
      } else if (Array.isArray(data.detail) && data.detail.length > 0) {
        const first = data.detail[0];
        errorMessage = first?.msg || errorMessage;
      }
    }
    const err = new Error(errorMessage) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return data;
};
